import { SCHEME } from './bindings'

// Authored markup, made safe to put in the admin's own document.
//
// **This is the one place this client renders content rather than
// describing it**, and it is worth being exact about why that needs a
// filter at all.
//
// A body is stored verbatim. `editors/tinymce` is configured to preserve
// rather than clean — `valid_elements: '*[*]'`, `verify_html: false`,
// `sanitize: false` — because the legacy corpus is page layout with
// `<script>` and `<style>` in it, and an editor that cleaned would delete
// an author's work on the first keystroke. That is the right decision for
// storage and it makes rich text a **trusted-input surface**: safe for
// exactly as long as every author is an operator.
//
// Delivery renders that markup on the site's own origin, which is the
// author's own site. This would render it on the *admin's* origin, next
// to the session that can write to every component in the tenant. The two
// are not the same risk, and the difference stops being theoretical the
// day `OP-04` lands and a customer — someone the operator does not
// employ — can type into the same field.
//
// So: scripts do not run, styles do not leak, and handlers do not fire.
// Not as a guess at malice, but because **a semantic preview has no use
// for any of them**. A `<style>` block written for the site would restyle
// the admin around it; a script would run against the wrong origin; an
// `onclick` would fire on a click the reader meant as "read this". Every
// one of them is noise in a reading even before it is a risk, which is
// what makes this a filter rather than a security control bolted on.
//
// What it is **not** is a claim to be a general HTML sanitiser. It is a
// deny-list over markup this platform stored, parsed by the browser's own
// parser rather than by a regular expression, and its failure mode is
// leaving something harmless in.

/** Elements whose content is code or presentation rather than words. */
const DROPPED = new Set(['script', 'style', 'link', 'meta', 'base', 'iframe', 'object', 'embed', 'noscript'])

/** Attributes that address something a browser will fetch or run. */
const URL_ATTRIBUTES = new Set(['href', 'src', 'srcset', 'action', 'formaction', 'poster', 'data', 'xlink:href'])

/**
 * The markup a node holds, as a string safe to set on an element in this
 * document.
 *
 * Parsed with `DOMParser` into an inert document — one with no browsing
 * context, so nothing in it loads, runs or fetches while it is being
 * walked. Setting the same string on a live element and cleaning it
 * afterwards would run it first, which is the mistake this exists to
 * avoid.
 */
export function markup(html: string, resolve?: (reference: string) => string | undefined): string {
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  clean(parsed.body, resolve)
  return parsed.body.innerHTML
}

/**
 * The `cms:` references a body makes, in document order.
 *
 * A body names its placed pictures and links by reference rather than by
 * address — `cms:<link id>@<component id>` — and the editor swaps in an
 * address while it edits. Anything else showing the body has to do the
 * same, or every bound picture is a broken image.
 */
export function boundReferences(html: string): { reference: string; linkId: string; target: string }[] {
  const parsed = new DOMParser().parseFromString(html, 'text/html')
  const found: { reference: string; linkId: string; target: string }[] = []
  for (const element of Array.from(parsed.body.querySelectorAll('*'))) {
    for (const attribute of Array.from(element.attributes)) {
      if (!attribute.value.startsWith(SCHEME)) continue
      const [linkId, target = ''] = attribute.value.slice(SCHEME.length).split('@')
      if (linkId) found.push({ reference: attribute.value, linkId, target })
    }
  }
  return found
}

function clean(root: Element, resolve?: (reference: string) => string | undefined) {
  // A static list, because removing during a live walk skips siblings.
  for (const element of Array.from(root.querySelectorAll('*'))) {
    if (DROPPED.has(element.tagName.toLowerCase())) {
      element.remove()
      continue
    }

    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase()

      // Every event handler, by shape rather than by a list of the
      // hundred there are.
      if (name.startsWith('on')) {
        element.removeAttribute(attribute.name)
        continue
      }

      // A `style` attribute is the same leak as a `<style>` element, one
      // element at a time. It is also where the corpus's inline colours
      // and fonts live — the ones `editors/tinymce` stopped offering
      // controls for — and they are written for the site's background,
      // not the admin's.
      if (name === 'style') {
        element.removeAttribute(attribute.name)
        continue
      }

      // A reference is replaced by the address it resolves to, and
      // dropped where it resolves to none: a browser would read `cms:` as
      // a scheme it cannot fetch, and a link to it goes nowhere.
      if (attribute.value.startsWith(SCHEME)) {
        const address = resolve?.(attribute.value)
        if (address) element.setAttribute(attribute.name, address)
        else element.removeAttribute(attribute.name)
        continue
      }

      if (URL_ATTRIBUTES.has(name) && runnable(attribute.value)) {
        element.removeAttribute(attribute.name)
      }
    }
  }
}

/**
 * Whether a URL executes rather than addresses.
 *
 * `javascript:` is the one that matters and `data:` is the one that
 * smuggles it — a `data:text/html` frame or anchor is a document this
 * origin would run. Leading whitespace and control characters are
 * stripped first, because a browser strips them before resolving the
 * scheme and a test that did not would be reading a different string
 * than the one that executes.
 */
function runnable(value: string): boolean {
  // eslint-disable-next-line no-control-regex
  const scheme = value.replace(/[ - ]/g, '').toLowerCase()
  return scheme.startsWith('javascript:') || scheme.startsWith('data:') || scheme.startsWith('vbscript:')
}

/**
 * Plain text as the paragraphs it was written as.
 *
 * A blank line is a paragraph break and a single newline is not, which is
 * the one convention every plain-text and markdown writer shares. It is
 * the whole of the handling markdown gets here, deliberately — see
 * `ContentPreview`.
 */
export function plainParagraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
}
